import React, { useContext, useState } from "react";
import { Routes, Route, useNavigate, Link, useLocation } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Home, Settings, LogOut, ClipboardList, Menu, CircleDollarSign, Info, FileText } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import IntakesList from "@/pages/dashboard/IntakesList";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import ServiceSheetView from "@/pages/dashboard/ServiceSheetView";
import SubscriptionPage from "./dashboard/SubscriptionPage";
import HelpGuide from "./HelpGuide";
import WorkOrdersList from "./dashboard/WorkOrdersList";

const Dashboard = () => {
  const { shop, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navItems = [
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/dashboard/intakes", icon: ClipboardList, label: "Intakes" },
    { path: "/dashboard/orders", icon: FileText, label: "Work Orders" },
    { path: "/dashboard/subscription", icon: CircleDollarSign, label: "Subscription" },
    { path: "/help-guide", icon: Info, label: "Help Guide", newTab: true },
    { path: "/dashboard/settings", icon: Settings, label: "Settings" },
  ];

  // Sidebar navigation component (reusable for both desktop and mobile)
  const SidebarNav = ({ onItemClick }) => (
    <nav className="p-4 space-y-2">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        const className = `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive
          ? "bg-[#00ced1]/20 text-[#00ced1] border border-[#00ced1]/30"
          : "text-[#f8f8f8]/70 hover:bg-[#005f6b]/20 hover:text-[#f8f8f8]"
          }`;

        if (item.newTab) {
          return (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              className={className}
              onClick={onItemClick}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </a>
          );
        }

        return (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            onClick={onItemClick}
            className={className}
          >
            <Icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#0a1a1f]">
      {/* Top Navigation */}
      <nav className="bg-[#005f6b]/20 backdrop-blur-sm border-b border-[#d4af37]/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            {/* Hamburger Menu - Only visible on mobile */}
            <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden text-[#d4af37] hover:bg-[#005f6b]/20"
                  data-testid="hamburger-menu"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetTitle></SheetTitle>
              <SheetContent
                side="left"
                className="bg-[#0a1a1f] border-[#d4af37]/20 w-64 p-0"
              >
                <div className="bg-[#005f6b]/10 backdrop-blur-sm h-full">
                  <div className="pt-6">
                    <SidebarNav onItemClick={() => setIsDrawerOpen(false)} />
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-full" />
            <span className="text-xl font-semibold text-[#d4af37]">{shop?.shop_name}</span>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="text-[#f8f8f8]/70 hover:text-[#00ced1] hover:bg-[#005f6b]/20"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar - Hidden on mobile, visible on desktop */}
        <aside className="hidden md:block w-64 bg-[#005f6b]/10 backdrop-blur-sm border-r border-[#d4af37]/20 min-h-[calc(100vh-73px)]">
          <SidebarNav onItemClick={() => { }} />
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="intakes" element={<IntakesList />} />
            <Route path="orders" element={<WorkOrdersList />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="service-sheet/:intakeId" element={<ServiceSheetView />} />
            {/* <Route path="help-guide" element={<HelpGuide />} /> */}
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
